import {useTranslation} from 'react-i18next'
import {FichaDatos as FichaDeLaCasa, type BloqueFicha} from '@/components/ui/ficha-datos'

/** `DatoRead` a mano: el detalle la arma desde `datos` + `datos_origen`. */
export type DatoDeLaFicha = {clave: string; valor: string | null; origen: string}

/** El orden del documento vive en el backend (`DOCE_DATOS`); aquí manda el orden de lectura. */
const BLOQUES = [
  {
    clave: 'persona',
    datos: ['trabajador', 'cargo', 'tipo_evaluacion', 'eps', 'afp', 'arl', 'lugar_atencion', 'fecha_estimada_ingreso'],
  },
  {
    clave: 'empresa',
    datos: ['empresa', 'nit', 'clase_riesgo', 'contacto_autoriza', 'bateria_autorizada'],
  },
] as const

const CONOCIDAS = new Set<string>(BLOQUES.flatMap((bloque) => bloque.datos as readonly string[]))

/** `porPersona` va vacío en una solicitud generada: sus datos están congelados y nada cambiaría. */
export function FichaDatos({
  datos,
  porPersona = [],
  testId,
}: {
  datos: DatoDeLaFicha[]
  porPersona?: readonly string[]
  testId?: string
}) {
  const {t} = useTranslation()
  // Claves literales: el gate `i18n-keys` no ve las armadas por plantilla.
  const TITULO: Record<string, string> = {
    persona: t('solicitudIps.ficha.persona'),
    empresa: t('solicitudIps.ficha.empresa'),
  }
  const bloques: BloqueFicha[] = BLOQUES.map((bloque, indice) => ({
    ...bloque,
    // El último bloque recoge los datos nuevos que agregue el backend.
    datos: datos.filter(
      (dato) =>
        (bloque.datos as readonly string[]).includes(dato.clave) ||
        (indice === BLOQUES.length - 1 && !CONOCIDAS.has(dato.clave))
    ),
  }))
    .filter((bloque) => bloque.datos.length > 0)
    .map((bloque) => ({
      clave: bloque.clave,
      titulo: TITULO[bloque.clave],
      testId: `solicitudIps-ficha-${bloque.clave}`,
      filas: bloque.datos.map((dato) => ({
        clave: dato.clave,
        etiqueta: t(`solicitudIps.datos.${dato.clave}`),
        valor: dato.valor ?? t('solicitudIps.datos.vacio'),
        marcada: porPersona.includes(dato.clave),
      })),
      procedencia: {
        label: t('solicitudIps.ficha.deDondeSalio'),
        testId: `solicitudIps-ficha-origen-${bloque.clave}`,
        contenidoTestId: `solicitudIps-ficha-origen-detalle-${bloque.clave}`,
        contenido: (
          <ul className="space-y-0.5">
            {bloque.datos.map((dato) => (
              <li key={dato.clave}>
                {t(`solicitudIps.datos.${dato.clave}`)}, {t(`solicitudIps.origen.${dato.origen}`)}
              </li>
            ))}
          </ul>
        ),
      },
    }))

  return (
    <FichaDeLaCasa
      bloques={bloques}
      marcador={porPersona.length > 0 ? {leyenda: t('solicitudIps.ficha.cambiaPorPersona')} : undefined}
      testId={testId}
    />
  )
}
